/**
 * shared/space_campaign.ts — CLIENT MIRROR of server/src/lib/space_campaign.ts
 * (the server copy is authoritative; keep them byte-identical below the
 * headers). Space Wave 4 — persistent operations: the fleet-war clock, the
 * forts, the finite reserves, the attrition round, the receipt points, the
 * objective shift, the incursion grip. Pure.
 */
import { warH } from './space_stations';

// ── THE CLOCK (the sky schedule and the client's visuals share it) ──────────
/** THE ENGAGEMENT LAW (2026-09-17, "the battle only lasted a few seconds"): a war is an HOUR's cycle
 *  with a 30-minute window, a line retreats no earlier than BATTLE_MIN_S in (a line with NO capitals
 *  excepted), attrition runs on the campaign's own clock (one round per ROUND_S), and a side that
 *  committed few capitals retreats at a third of them, not at the absolute three. */
export const BATTLE_PERIOD_S = 3600;
export const BATTLE_LEN_S = 1800;
export const BATTLE_MIN_S = 900;
export const ROUND_S = 60;
export const BATTLE_END_S = 32;
export const BATTLE_CAPS = 10;
export const WAR_SKIRMISHERS = 30;
export const WAR_ZONE_M = 26_000;

export interface FortSpec { key: string; name: string; pos: [number, number, number]; radius: number }
/** The Commons Reach's forts in space_sector.ts POI order (kind fortress | megastation). */
export const SYS0_FORTS: readonly FortSpec[] = [
  { key: 'poi9', name: 'Redhand Fortress', pos: [-1_050_000, 320_000, 850_000], radius: 6_000 },
  { key: 'poi12', name: 'Aurel Prime', pos: [9_500, 900, 5_200], radius: 5_200 },
  { key: 'poi27', name: 'Blackharrow Bastion', pos: [700_000, -320_000, -1_350_000], radius: 7_000 },
];
export function campaignWindow(nowS: number): { cyc: number; t: number } | null {
  const cyc = Math.floor(nowS / BATTLE_PERIOD_S);
  const t = nowS - cyc * BATTLE_PERIOD_S;
  return t > BATTLE_LEN_S ? null : { cyc, t };
}
export function fortOf(cyc: number): FortSpec { return SYS0_FORTS[cyc % SYS0_FORTS.length]; }
export function battleCentre(f: FortSpec): [number, number, number] { return [f.pos[0] + 1.6 * f.radius, f.pos[1] + 400, f.pos[2]]; }
/** The system this cycle's campaign decides (the client's hash, unchanged). */
export function warTargetOf(cyc: number): string { return `sys${1 + (Math.floor(warH(`wt${cyc}`) * 100) % 100)}`; }

// ── THE STORE LAW ────────────────────────────────────────────────────────────
export const CIVS = ['aurelian', 'borean'] as const;
export type Civ = (typeof CIVS)[number];
export const RESERVE_SEED = { hulls: 60, capitals: 12 } as const;
export const RESERVE_HULLS_MAX = 120;
export const RESERVE_CAPS_MAX = 24;
export const YARD_HULLS_TO_RESERVE_PER_H = 6;
/** THE YARD'S INCOME LAW (2026-09-17): the civ's store PAYS the yard for every hull it takes — the only
 *  income a yard has (it pays the miner and escrows supply work; a purse that only drains ran to 1,200 ⓒ). */
export const HULL_SALE_SOV = 2_000n;
export const BOREAN_TRICKLE_HULLS_PER_H = 6;
/** THE RESERVE FLOOR: a civ below this is fed the trickle whatever its yard does, and builds a capital
 *  from ten hulls an hour — an empty store made every war a round-one retreat. */
export const RESERVE_FLOOR = { hulls: 30, capitals: 6 } as const;
export const HULLS_PER_CAPITAL = 10;
/** What a reserve can field: capitals first (hulls convert ten to one when short), then gunships. */
export function commitFromReserve(hulls: number, capitals: number): { caps: number; skirm: number; hullsLeft: number; capsLeft: number } {
  let caps = Math.min(BATTLE_CAPS, capitals);
  let hullsLeft = hulls;
  while (caps < BATTLE_CAPS && hullsLeft >= HULLS_PER_CAPITAL + WAR_SKIRMISHERS) { caps++; hullsLeft -= HULLS_PER_CAPITAL; }
  const skirm = Math.min(WAR_SKIRMISHERS, hullsLeft);
  return { caps, skirm, hullsLeft: hullsLeft - skirm, capsLeft: capitals - Math.min(BATTLE_CAPS, capitals) };
}

// ── THE ROUND LAW ────────────────────────────────────────────────────────────
export const ROUND_SK_PER_ENEMY_CAP = 0.12;
/** Tuned for the minute-round: ten enemy capitals cost a line ~0.35 capitals a minute, so a full
 *  line reaches the retreat threshold in ~20 minutes (15 is the floor, 30 the window). */
export const ROUND_CAP_P_PER_ENEMY_CAP = 0.035;
export const RETREAT_CAPS = 3;
/** A side retreats at RETREAT_CAPS, or at a THIRD of what it committed when that is fewer (a
 *  three-capital line fights to its last one instead of retreating at round one). */
export function retreatThreshold(capsCommitted: number): number {
  return Math.min(RETREAT_CAPS, Math.floor(capsCommitted / 3));
}
export function mayRetreat(capsAlive: number, capsCommitted: number, elapsedS: number, margin = 0): boolean {
  if (capsAlive <= 0) return true;
  return elapsedS >= BATTLE_MIN_S && capsAlive <= retreatThreshold(capsCommitted) + margin; // a WITHDRAW stance retreats a capital earlier
}
/** Rounds owed on the campaign's clock (one per ROUND_S since it opened) beyond those already run. */
export function roundsDue(openedAtS: number, nowS: number, done: number): number {
  return Math.max(0, Math.floor((nowS - openedAtS) / ROUND_S) - done);
}
/** One deterministic attrition round: losses each side takes from the OTHER side's living capitals. */
export function attritionRound(campaignId: bigint, round: number, capsAlive: readonly [number, number], mult: readonly [number, number] = [1, 1]): { sk: [number, number]; cap: [number, number] } {
  const sk: [number, number] = [0, 0], cap: [number, number] = [0, 0];
  for (const s of [0, 1] as const) {
    const enemyCaps = capsAlive[1 - s];
    // Space Wave 7 (FLEET COMMAND): `mult[s]` scales the losses side s TAKES (its stance's `take` × the enemy stance's `give`).
    const exp = ROUND_SK_PER_ENEMY_CAP * enemyCaps * mult[s];
    sk[s] = Math.floor(exp) + (warH(`${campaignId}|${round}|${s}|sk`) < exp - Math.floor(exp) ? 1 : 0);
    cap[s] = warH(`${campaignId}|${round}|${s}|cap`) < ROUND_CAP_P_PER_ENEMY_CAP * enemyCaps * mult[s] ? 1 : 0;
  }
  return { sk, cap };
}

// ── THE RECEIPT / OBJECTIVE LAWS ─────────────────────────────────────────────
export const CAMPAIGN_POINTS = { gunship: 1, capital: 10 } as const;
export const CAMPAIGN_POINTS_CAP = 40;
export const CAMPAIGN_PAY_SOV_PER_POINT = 25n;
export const CAMPAIGN_SOV_SHIFT = 12;
export const CAMPAIGN_REAP_S = 7 * 86_400;
/** war<side>:sk<serial> (a gunship) | cap<side>:<k> (a capital). */
export const HULL_KEY_RE = /^(war[01]:sk\d{1,6}|cap[01]:\d{1,2})$/;
export function hullSide(key: string): 0 | 1 { return key.startsWith('war1') || key.startsWith('cap1') ? 1 : 0; }
export function hullKind(key: string): 'gunship' | 'capital' { return key.startsWith('cap') ? 'capital' : 'gunship'; }
export function civOfSide(side: number): Civ { return side === 0 ? 'aurelian' : 'borean'; }

// ── THE INCURSION LAW ────────────────────────────────────────────────────────
export const INCURSION_GRIP_PER_KILL = 2;
export const INCURSION_BOSS_GRIP = 25;
